
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import dateFormat from 'dateformat';
import { PurchaseOrderModel } from '@/models/purchase_order';
import purchaseOrderApprovalService from '@/services/purchaseOrderApproval.service';
import ApprovalDialog from './ApprovalDialog.vue';
import RejectionDialog from './RejectionDialog.vue';
import UnapprovedPOsModule from '@/store/modules/unapproved-pos';
import { EventBus } from '@/main';
import { SupplierModel } from '@/models/supplier';
import { FilterColumnModel } from '@/models/filter_column';
import { FilterColumnList } from '@/collections/filter_columns';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { isUsableImageUrl } from '@/helpers/variantImage';

@Component({
  components: {
    ApprovalDialog,
    RejectionDialog,
  },
})
export default class PODetailModal extends Vue {
  /**
   * The usable-image rule, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private isUsableImageUrl = isUsableImageUrl;

  @Prop({ type: Boolean, default: false }) private value!: boolean;
  @Prop({ type: Number, default: null }) private poId!: number | null;
  @Prop({ type: Object, default: null }) private poData!: any;

  private unapprovedPOsStore = getModule(UnapprovedPOsModule, this.$store);

  private show = false;
  private loading = false;
  private approving = false;
  private rejecting = false;
  private purchaseOrder: any = null;
  private approvalHistory: any[] = [];
  private showApprovalDialog = false;
  private showRejectionDialog = false;
  private showQuantityWarningConfirm = false;
  private quantityWarningResolve: ((value: boolean) => void) | null = null;
  
  // Quantity adjustment dialog state
  private adjustedQuantities: { [itemId: number]: number } = {};
  private itemsToRemove: number[] = [];
  private adjustmentValidationErrors: string[] = [];
  private applyingAdjustments = false;
  
  // Quantity validation
  private supplier: SupplierModel | null = null;
  private variantOrderConstraints: { [variantId: number]: { min: number; max: number } } = {};
  private loadingConstraints = false;
  private validationCompleted = false;
  private checkingValidation = false;
  private quantityValidationErrors: { [itemId: number]: string } = {};

  private lineItemHeaders = [
    { text: 'Image', value: 'variantImage', sortable: false, width: '80px' },
    { text: 'Product', value: 'productName' },
    { text: 'Variant', value: 'variant' },
    { text: 'SKU', value: 'sku' },
    { text: 'Quantity', value: 'orderedQuantity', align: 'center', width: '120px' },
    { text: 'Unit Price', value: 'unitPrice', align: 'right' },
    { text: 'Total', value: 'totalPrice', align: 'right' },
  ];

  get lineItems() {
    if (!this.purchaseOrder || !this.purchaseOrder.items) { return []; }
    return this.purchaseOrder.items;
  }
  
  get hasQuantityErrors(): boolean {
    return Object.keys(this.quantityValidationErrors).length > 0;
  }
  
  get quantityErrorItems() {
    if (!this.purchaseOrder?.items) return [];
    return this.purchaseOrder.items.filter((item: any) => 
      item.id && this.quantityValidationErrors[item.id]
    );
  }
  
  get canApplyAdjustments(): boolean {
    // Check if there are any adjustments to apply
    const hasAdjustments = Object.keys(this.adjustedQuantities).length > 0 || 
                          this.itemsToRemove.length > 0;
    // Check if there are no validation errors
    const noErrors = this.adjustmentValidationErrors.length === 0;
    return hasAdjustments && noErrors;
  }

  @Watch('value')
  private onValueChange(val: boolean): void {
    this.show = val;
    if (val && (this.poId || this.poData)) {
      // Check if we should show validation overlay immediately
      const hasSupplier = this.poData?.supplierId || (this.poId && !this.poData);
      if (hasSupplier) {
        this.loadingConstraints = true; // Show overlay immediately
      }
      this.loadPurchaseOrder();
    }
  }

  @Watch('show')
  private onShowChange(val: boolean): void {
    this.$emit('input', val);
    if (!val) {
      this.purchaseOrder = null;
      this.approvalHistory = [];
      this.supplier = null;
      this.variantOrderConstraints = {};
      this.quantityValidationErrors = {};
      this.validationCompleted = false;
      this.loadingConstraints = false; // Reset loading state
    }
  }

  @Watch('poId')
  private onPoIdChange(val: number | null): void {
    if (val && this.show) {
      // Show validation overlay immediately for new PO
      this.loadingConstraints = true;
      this.loadPurchaseOrder();
    }
  }

  private async loadPurchaseOrder(): Promise<void> {
    // If we have poData passed as prop, use it
    if (this.poData) {
      // console.log('PODetailModal - Loading with poData:', this.poData);
      this.purchaseOrder = this.poData;
      // If we need more details, we might need to fetch the full PO
      if (!this.poData.items) {
        await this.fetchFullPurchaseOrder();
      } else {
        // Fetch constraints (overlay already shown)
        if (this.purchaseOrder.supplierId) {
          await this.fetchVariantOrderConstraints();
        }
      }
      return;
    }

    // Otherwise fetch by ID
    if (this.poId) {
      await this.fetchFullPurchaseOrder();
    }
  }

  private async fetchFullPurchaseOrder(): Promise<void> {
    if (!this.poId) { return; }

    this.loading = true;
    try {
      // Fetch the full purchase order details
      const po = await PurchaseOrderModel.getById(this.poId);
      // console.log('PODetailModal - Fetched PO:', po);
      this.purchaseOrder = {
        ...po.toJSON(),
        supplierName: po.supplierName,
        locationName: po.locationName,
        automationConfigName: this.poData?.automationConfigName || 'Automation Config',
        daysSinceGeneration: this.poData?.daysSinceGeneration || this.calculateDaysSince(po.createdAt),
      };
      // console.log('PODetailModal - Final purchaseOrder:', this.purchaseOrder);

      // Fetch constraints (overlay already shown)
      if (this.purchaseOrder.supplierId) {
        await this.fetchVariantOrderConstraints();
      }

      // TODO: Fetch approval history if API is available
      // this.approvalHistory = await this.fetchApprovalHistory(this.poId);
    } catch (error) {
      // console.error('Error loading purchase order:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to load purchase order details',
        color: 'error'
      });
      this.close();
    } finally {
      this.loading = false;
    }
  }

  private calculateDaysSince(date: string | undefined): number {
    if (!date) { return 0; }
    const created = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  private formatDate(date: string): string {
    if (!date) { return 'N/A'; }
    return dateFormat(new Date(date), 'mmm d, yyyy');
  }

  private formatDateTime(date: string): string {
    if (!date) { return 'N/A'; }
    return dateFormat(new Date(date), 'mmm d, yyyy h:MM TT');
  }

  private formatCurrencyValue(amount: any): string {
    const currencyCode = this.purchaseOrder?.currencyCode || (this.purchaseOrder as any)?.currency_code || null;
    if (currencyCode) {
      return CurrencyHelper.formatCurrencyWithCode(Number(amount) || 0, currencyCode);
    }
    return CurrencyHelper.formatCurrency(amount);
  }

  private getStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      draft: 'Draft',
      pending_approval: 'Pending Approval', // US-037
      sent: 'Sent to Supplier',
      confirmed: 'Confirmed',
      partial_received: 'Partially Received',
      received: 'Fully Received',
      canceled: 'Canceled',
      rejected: 'Rejected',
    };
    return statusMap[status] || status;
  }

  private getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      draft: 'grey',
      pending_approval: 'orange', // US-037
      sent: 'blue',
      confirmed: 'amber',
      partial_received: 'light-green',
      received: 'green',
      canceled: 'red',
      rejected: 'red',
    };
    return colorMap[status] || 'grey';
  }

  private getApproveButtonText(): string {
    if (this.hasQuantityErrors && this.validationCompleted) {
      const errorCount = Object.keys(this.quantityValidationErrors).length;
      if (errorCount === 1) {
        return 'Review & Fix Quantity Issue';
      } else {
        return `Review & Fix ${errorCount} Quantity Issues`;
      }
    }
    return 'Approve';
  }

  private async handleApprove(): Promise<void> {
    try {
      // First check if validation has been completed
      if (!this.validationCompleted && this.purchaseOrder?.supplierId) {
        // Show checking validation state
        this.checkingValidation = true;
        // If not validated yet, trigger validation first
        await this.fetchVariantOrderConstraints();
        this.checkingValidation = false;
      }
      
      // Check if there are quantity validation errors
      if (this.hasQuantityErrors) {
        // Show a confirmation dialog about the validation issues
        const proceed = await this.showQuantityWarningDialog();
        if (!proceed) {
          return; // User cancelled
        }
      }
      
      this.showApprovalDialog = true;
    } catch (error) {
      console.error('Error during validation check:', error);
      // Even if validation fails, allow approval to proceed
      this.showApprovalDialog = true;
    } finally {
      this.checkingValidation = false;
    }
  }
  
  private async reloadPurchaseOrderData(): Promise<void> {
    try {
      // Reload only the PO data without fetching constraints
      await this.fetchPurchaseOrderDataOnly();
      
      // Re-validate with existing constraints
      this.validateAllItems();
      
    } catch (error) {
      console.error('Error reloading purchase order data:', error);
      // Don't show error to user as this is background refresh
    }
  }
  
  private async fetchPurchaseOrderDataOnly(): Promise<void> {
    if (!this.poId) { return; }

    try {
      // Fetch the full purchase order details
      const po = await PurchaseOrderModel.getById(this.poId);
      this.purchaseOrder = {
        ...po.toJSON(),
        supplierName: po.supplierName,
        locationName: po.locationName,
        automationConfigName: this.poData?.automationConfigName || 'Automation Config',
        daysSinceGeneration: this.poData?.daysSinceGeneration || this.calculateDaysSince(po.createdAt),
      };
      
      // Do NOT fetch constraints here - they are already loaded
      
    } catch (error) {
      console.error('Error fetching PO data:', error);
      throw error;
    }
  }

  private handleReject(): void {
    this.showRejectionDialog = true;
  }

  // US-037: Updated to accept approvalType
  private async confirmApprove(data: { notes?: string; approvalType?: string }): Promise<void> {
    this.showApprovalDialog = false;
    this.approving = true;
    try {
      await purchaseOrderApprovalService.approveSingle(this.purchaseOrder.id, {
        notes: data.notes,
        approvalType: data.approvalType as 'approve_only' | 'approve_and_confirm'
      });
      this.$emit('approved', this.purchaseOrder.id);

      // Show appropriate message based on approval type
      const statusMessage = data.approvalType === 'approve_only'
        ? 'converted to draft'
        : 'confirmed';
      EventBus.$emit('show-snackbar', {
        message: `Purchase order ${this.purchaseOrder.poNumber} ${statusMessage} successfully`,
        color: 'success'
      });
      this.close();
      // Refresh the list
      this.$emit('refresh');
    } catch (error: any) {
      // console.error('Error approving PO:', error);
      EventBus.$emit('show-snackbar', {
        message: error.message || 'Failed to approve purchase order',
        color: 'error'
      });
    } finally {
      this.approving = false;
    }
  }

  private async confirmReject(data: { reason: string }): Promise<void> {
    this.showRejectionDialog = false;
    this.rejecting = true;
    try {
      await purchaseOrderApprovalService.rejectSingle(this.purchaseOrder.id, data.reason);
      this.$emit('rejected', this.purchaseOrder.id);
      EventBus.$emit('show-snackbar', {
        message: `Purchase order ${this.purchaseOrder.poNumber} rejected`,
        color: 'success'
      });
      this.close();
      // Refresh the list
      this.$emit('refresh');
    } catch (error: any) {
      // console.error('Error rejecting PO:', error);
      EventBus.$emit('show-snackbar', {
        message: error.message || 'Failed to reject purchase order',
        color: 'error'
      });
    } finally {
      this.rejecting = false;
    }
  }

  private close(): void {
    this.show = false;
  }

  // Quantity validation methods
  private async fetchVariantOrderConstraints(): Promise<void> {
    if (!this.purchaseOrder?.supplierId) return;
    
    // Set loading state if not already set by caller
    if (!this.loadingConstraints) {
      this.loadingConstraints = true;
    }
    
    try {
      // Fetch supplier details first
      if (!this.supplier) {
        this.supplier = await SupplierModel.getById(this.purchaseOrder.supplierId);
      }
      
      // Get variant IDs from purchase order items
      const variantIds = this.purchaseOrder.items ? 
        this.purchaseOrder.items.map((item: any) => item.variantId).filter(Boolean) : [];
      
      if (variantIds.length === 0) {
        return;
      }
      
      // Create filter for variant IDs
      const filterColumns = new FilterColumnList();
      const variantFilter = new FilterColumnModel({
        code: 'variant_id',
        operator: 'in',
        dataType: 'number',
        value: variantIds
      });
      filterColumns.add(variantFilter);
      
      // Fetch data using supplier's fetchProductsWithOptions
      const report = await this.supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        'eq',
        {
          initialFilterColumns: filterColumns,
          pageOptions: {
            page: 1,
            itemsPerPage: 1000
          },
          columns: [
            { code: 'variant_id', dataType: 'number', name: 'Variant ID' },
            { code: '[variant_min_order_quantity]', dataType: 'number', name: 'Min Order Qty' },
            { code: '[variant_max_order_quantity]', dataType: 'number', name: 'Max Order Qty' }
          ],
          suggestFutureReorderQuantity: true
        }
      );
      
      // Clear existing constraints
      this.variantOrderConstraints = {};
      
      // Parse report data using getReportRowsData() method
      const reportData = report.getReportRowsData();
      
      // Store constraints in map
      if (reportData && reportData.length > 0) {
        reportData.forEach((row: any) => {
          const variantId = row['variant_id'];
          const minQty = parseFloat(row['[variant_min_order_quantity]']) || 0;
          const maxQty = parseFloat(row['[variant_max_order_quantity]']) || 999999;
          
          if (variantId) {
            this.variantOrderConstraints[variantId] = {
              min: minQty,
              max: maxQty === 0 ? 999999 : maxQty  // Treat 0 max as no limit
            };
          }
        });
      }
      
      // Validate all items after loading constraints
      this.validateAllItems();
    } catch (error: any) {
      console.error('Error fetching variant order constraints:', error);
      // Don't show error to user, constraints are optional
    } finally {
      this.loadingConstraints = false;
      this.validationCompleted = true;
    }
  }

  private validateItemQuantity(item: any): void {
    if (!item.id) return;

    // US-037: Changed from 'draft' to 'pending_approval'
    if (this.purchaseOrder?.status !== 'pending_approval') {
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) {
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const qty = item.orderedQuantity;
    
    if (qty < constraint.min) {
      Vue.set(this.quantityValidationErrors, item.id, `Minimum order quantity is ${constraint.min}`);
    } else if (qty > constraint.max && constraint.max < 999999) {
      Vue.set(this.quantityValidationErrors, item.id, `Maximum order quantity is ${constraint.max}`);
    } else {
      Vue.delete(this.quantityValidationErrors, item.id);
    }
  }

  private validateAllItems(): void {
    if (!this.purchaseOrder?.items) return;

    this.quantityValidationErrors = {};

    // US-037: Changed from 'draft' to 'pending_approval'
    if (this.purchaseOrder?.status !== 'pending_approval') return;
    
    this.purchaseOrder.items.forEach((item: any) => {
      if (item.id && item.variantId) {
        this.validateItemQuantity(item);
      }
    });
  }

  private getQuantityError(item: any): string {
    // US-037: Changed from 'draft' to 'pending_approval'
    if (this.purchaseOrder?.status !== 'pending_approval') return '';
    return item.id ? (this.quantityValidationErrors[item.id] || '') : '';
  }

  private getQuantityHint(item: any): string {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) return '';
    
    if (constraint.max >= 999999) {
      return constraint.min > 0 ? `Min: ${constraint.min}` : '';
    } else {
      return `Min: ${constraint.min}, Max: ${constraint.max}`;
    }
  }

  private showQuantityWarningDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      // Initialize adjusted quantities with current values for items with errors
      this.adjustedQuantities = {};
      this.itemsToRemove = [];
      this.adjustmentValidationErrors = [];
      
      // Pre-populate adjusted quantities with current values
      this.quantityErrorItems.forEach((item: any) => {
        Vue.set(this.adjustedQuantities, item.id, item.orderedQuantity);
      });
      
      this.showQuantityWarningConfirm = true;
      // Store the resolve function to be called when dialog closes
      this.quantityWarningResolve = resolve;
    });
  }

  private onQuantityWarningConfirm(proceed: boolean): void {
    this.showQuantityWarningConfirm = false;
    if (this.quantityWarningResolve) {
      this.quantityWarningResolve(proceed);
      this.quantityWarningResolve = null;
    }
  }
  
  // Quantity adjustment methods
  private getItemConstraintRange(item: any): string {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) return 'N/A';
    
    if (constraint.max >= 999999) {
      return `Min: ${constraint.min}`;
    } else {
      return `${constraint.min} - ${constraint.max}`;
    }
  }
  
  private getItemMaxConstraint(item: any): number | null {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint || constraint.max >= 999999) return null;
    return constraint.max;
  }
  
  private adjustToMin(item: any): void {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (constraint) {
      const newAdjusted = { ...this.adjustedQuantities };
      newAdjusted[item.id] = constraint.min;
      this.adjustedQuantities = newAdjusted;
      this.validateAdjustedQuantity(item);
    }
  }
  
  private adjustToMax(item: any): void {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (constraint && constraint.max < 999999) {
      const newAdjusted = { ...this.adjustedQuantities };
      newAdjusted[item.id] = constraint.max;
      this.adjustedQuantities = newAdjusted;
      this.validateAdjustedQuantity(item);
    }
  }
  
  private toggleRemoveItem(item: any): void {
    const index = this.itemsToRemove.indexOf(item.id);
    if (index > -1) {
      this.itemsToRemove.splice(index, 1);
      // Restore original quantity when un-removing
      const newAdjusted = { ...this.adjustedQuantities };
      newAdjusted[item.id] = item.orderedQuantity;
      this.adjustedQuantities = newAdjusted;
    } else {
      this.itemsToRemove.push(item.id);
      // Clear adjusted quantity when removing
      const newAdjusted = { ...this.adjustedQuantities };
      delete newAdjusted[item.id];
      this.adjustedQuantities = newAdjusted;
    }
    this.validateAllAdjustments();
  }
  
  private onQuantityInput(item: any, value: any): void {
    const numValue = parseFloat(value) || 0;
    
    // Force reactivity by creating a new object
    const newAdjusted = { ...this.adjustedQuantities };
    newAdjusted[item.id] = numValue;
    this.adjustedQuantities = newAdjusted;
    
    this.validateAdjustedQuantity(item);
  }
  
  private validateAdjustedQuantity(_item: any): void {
    this.validateAllAdjustments();
  }
  
  private validateAllAdjustments(): void {
    this.adjustmentValidationErrors = [];
    
    // Validate each adjusted quantity
    for (const itemId in this.adjustedQuantities) {
      const item = this.purchaseOrder.items.find((i: any) => i.id === parseInt(itemId));
      if (!item) continue;
      
      const newQty = this.adjustedQuantities[itemId];
      if (newQty < 0) {
        this.adjustmentValidationErrors.push(
          `${item.productName}: Quantity cannot be negative`
        );
      } else if (newQty === 0 && !this.itemsToRemove.includes(item.id)) {
        this.adjustmentValidationErrors.push(
          `${item.productName}: Use remove action instead of setting quantity to 0`
        );
      }
    }
    
    // Check if all items would be removed
    if (this.itemsToRemove.length === this.purchaseOrder.items.length) {
      this.adjustmentValidationErrors.push(
        'Cannot remove all items from the purchase order'
      );
    }
  }
  
  private cancelQuantityAdjustment(): void {
    // Reset adjustment state
    this.adjustedQuantities = {};
    this.itemsToRemove = [];
    this.adjustmentValidationErrors = [];
    this.onQuantityWarningConfirm(false);
  }
  
  private proceedWithoutChanges(): void {
    // Reset adjustment state and proceed
    this.adjustedQuantities = {};
    this.itemsToRemove = [];
    this.adjustmentValidationErrors = [];
    this.onQuantityWarningConfirm(true);
  }
  
  private async applyAdjustments(): Promise<void> {
    this.applyingAdjustments = true;
    try {
      // Don't modify this.purchaseOrder.items here - let the save method handle it
      
      // Save the adjusted quantities to the purchase order
      
      // Save changes to the purchase order
      await this.savePurchaseOrderChanges(this.adjustedQuantities, this.itemsToRemove);
      
      EventBus.$emit('show-snackbar', {
        message: 'Quantities updated successfully',
        color: 'success'
      });
      
      // Reset adjustment state
      this.adjustedQuantities = {};
      this.itemsToRemove = [];
      this.adjustmentValidationErrors = [];
      
      // Close warning dialog
      this.showQuantityWarningConfirm = false;
      
      // Reload PO data to get fresh state
      await this.reloadPurchaseOrderData();
      
    } catch (error) {
      console.error('Error applying adjustments:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to apply quantity adjustments',
        color: 'error'
      });
    } finally {
      this.applyingAdjustments = false;
    }
  }
  
  private async savePurchaseOrderChanges(adjustedQuantities: { [itemId: number]: number }, itemsToRemove: number[]): Promise<void> {
    // Save the modified purchase order
    try {
      // Get fresh PO model from database
      const po = await PurchaseOrderModel.getById(this.purchaseOrder.id);


      // Update individual item quantities using adjustedQuantities parameter
      for (const itemId in adjustedQuantities) {
        const originalItem = po.items.find((item: any) => parseInt(itemId) === parseInt(item.id));
        if (originalItem && !itemsToRemove.includes(parseInt(itemId))) {
          const newQuantity = adjustedQuantities[itemId];
          originalItem.orderedQuantity = newQuantity;
          originalItem.totalPrice = newQuantity * originalItem.unitPrice;
          originalItem._action = 'update';
        }
      }

      // Mark items for removal by setting _action = 'remove'
      if (itemsToRemove.length > 0) {
        po.items.forEach((item: any) => {
          const itemIdNum = Number(item.id);
          const shouldRemove = itemsToRemove.some(removeId => Number(removeId) === itemIdNum);
          if (shouldRemove) {
            item._action = 'remove';
          }
        });
      }

      // Recalculate total amount based on items that are NOT being removed
      po.totalAmount = po.items
        .filter((item: any) => item._action !== 'remove')
        .reduce(
          (sum: number, item: any) => sum + (item.totalPrice || 0),
          0
        );



      // Save changes to database
      await po.save();


    } catch (error) {
      console.error('Error saving PO changes:', error);
      throw error;
    }
  }
}
